import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Calendar, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";

export function InsuranceManager() {
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("insurance_policies")
      .select("*")
      .order("end_date", { ascending: true });

    if (error) {
      toast.error("Failed to load insurance policies");
      setIsLoading(false);
      return;
    }

    setPolicies(data || []);
    setIsLoading(false);
  };

  const getStatusBadge = (policy: any) => {
    const today = new Date();
    const endDate = new Date(policy.end_date);
    const daysUntilExpiry = differenceInDays(endDate, today);

    if (policy.status === "expired") {
      return <Badge variant="destructive">Expired</Badge>;
    }

    if (daysUntilExpiry < 0) {
      return <Badge variant="destructive">Expired</Badge>;
    }

    if (daysUntilExpiry <= 30) {
      return <Badge className="bg-orange-100 text-orange-800">Expiring Soon</Badge>;
    }

    if (policy.status === "active") {
      return <Badge className="bg-green-100 text-green-800">Active</Badge>;
    }

    return <Badge variant="secondary">{policy.status}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Insurance Policies</CardTitle>
              <CardDescription>
                Manage business insurance policies and renewals
              </CardDescription>
            </div>
            <Button>Add Policy</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">Loading insurance policies...</div>
            ) : policies.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No insurance policies added yet. Add your first policy to get started.
              </div>
            ) : (
              policies.map((policy) => (
                <Card key={policy.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-primary" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{policy.policy_type}</h3>
                            {getStatusBadge(policy)}
                          </div>
                          <p className="text-sm text-muted-foreground">{policy.provider_name}</p>
                          {policy.policy_number && (
                            <p className="text-sm text-muted-foreground">
                              Policy #: {policy.policy_number}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Coverage:</span>{" "}
                              <span className="font-medium">
                                {formatCurrency(policy.coverage_amount)}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Premium:</span>{" "}
                              <span className="font-medium">
                                {formatCurrency(policy.premium_amount)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span className="text-muted-foreground">Start:</span>{" "}
                              {format(new Date(policy.start_date), "dd MMM yyyy")}
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span className="text-muted-foreground">End:</span>{" "}
                              {format(new Date(policy.end_date), "dd MMM yyyy")}
                            </div>
                            {policy.renewal_date && (
                              <div className="flex items-center gap-1">
                                <AlertCircle className="h-3 w-3 text-orange-500" />
                                <span className="text-muted-foreground">Renewal:</span>{" "}
                                {format(new Date(policy.renewal_date), "dd MMM yyyy")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline">
                        View Details
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
